import { useState, useCallback, useEffect, useRef } from 'react';
import { api } from '@/lib/api';
import type { FiardleGuessData } from '@/lib/api';
import type { KeyState } from '../components/Keyboard';

interface FiardleGameState {
  gameId: string | null;
  guesses: FiardleGuessData[];
  status: 'idle' | 'in_progress' | 'won' | 'lost';
  answer: string | null;
  currentGuess: string;
  letterStates: Record<string, KeyState>;
  loading: boolean;
  error: string | null;
  submitting: boolean;
}

export function useFiardleGame() {
  const fetchIdRef = useRef(0);
  const submittingRef = useRef(false);
  const stateRef = useRef<FiardleGameState | null>(null);
  const [state, setState] = useState<FiardleGameState>({
    gameId: null,
    guesses: [],
    status: 'idle',
    answer: null,
    currentGuess: '',
    letterStates: {},
    loading: true,
    error: null,
    submitting: false,
  });

  // Keep ref in sync for use in submitGuess
  stateRef.current = state;

  const computeLetterStates = useCallback(
    (guesses: FiardleGuessData[]): Record<string, KeyState> => {
      const states: Record<string, KeyState> = {};
      for (const guess of guesses) {
        guess.word.split('').forEach((letter, i) => {
          const result = guess.result[i] as KeyState;
          const current = states[letter];
          // Priority: green > yellow > gray
          if (!current || result === 'green' || (result === 'yellow' && current !== 'green')) {
            states[letter] = result;
          }
        });
      }
      return states;
    },
    [],
  );

  const loadActiveGame = useCallback(async () => {
    const id = ++fetchIdRef.current;
    setState((s) => ({ ...s, loading: true, error: null }));

    try {
      const result = await api.getFiardleActiveGame();
      if (fetchIdRef.current !== id) return;

      if (result.game) {
        setState((s) => ({
          ...s,
          gameId: result.game.gameId,
          guesses: result.game.guesses || [],
          status: 'in_progress',
          letterStates: computeLetterStates(result.game.guesses || []),
          loading: false,
        }));
      } else {
        setState((s) => ({ ...s, status: 'idle', loading: false }));
      }
    } catch {
      if (fetchIdRef.current !== id) return;
      // 404 means no active game - that's fine
      setState((s) => ({ ...s, status: 'idle', loading: false }));
    }
  }, [computeLetterStates]);

  const startNewGame = useCallback(async () => {
    const id = ++fetchIdRef.current;
    setState((s) => ({ ...s, loading: true, error: null }));

    try {
      const result = await api.createFiardleGame();
      if (fetchIdRef.current !== id) return;

      setState({
        gameId: result.gameId,
        guesses: [],
        status: 'in_progress',
        answer: null,
        currentGuess: '',
        letterStates: {},
        loading: false,
        error: null,
        submitting: false,
      });
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setState((s) => ({
        ...s,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to create game',
      }));
    }
  }, []);

  const submitGuess = useCallback(async () => {
    const current = stateRef.current;
    if (!current || !current.gameId || current.currentGuess.length !== 5 || submittingRef.current)
      return;

    // Use ref to guard against concurrent submissions
    submittingRef.current = true;
    setState((s) => ({ ...s, submitting: true, error: null }));

    const { gameId, currentGuess } = current;

    try {
      const result = await api.submitFiardleGuess(gameId, currentGuess);

      setState((s) => {
        const newGuesses = [...s.guesses, result.guess];
        return {
          ...s,
          guesses: newGuesses,
          currentGuess: '',
          letterStates: computeLetterStates(newGuesses),
          status: result.game.status as FiardleGameState['status'],
          answer: result.game.answer || null,
          submitting: false,
        };
      });
    } catch (err) {
      setState((s) => ({
        ...s,
        submitting: false,
        error: err instanceof Error ? err.message : 'Failed to submit guess',
      }));
    } finally {
      submittingRef.current = false;
    }
  }, [computeLetterStates]);

  const onKeyPress = useCallback(
    (key: string) => {
      if (state.status !== 'in_progress' || state.submitting) return;
      setState((s) => {
        if (s.currentGuess.length >= 5) return s;
        return { ...s, currentGuess: s.currentGuess + key, error: null };
      });
    },
    [state.status, state.submitting],
  );

  const onBackspace = useCallback(() => {
    if (state.status !== 'in_progress' || state.submitting) return;
    setState((s) => ({
      ...s,
      currentGuess: s.currentGuess.slice(0, -1),
      error: null,
    }));
  }, [state.status, state.submitting]);

  const onEnter = useCallback(() => {
    if (state.currentGuess.length === 5) {
      submitGuess();
    }
  }, [state.currentGuess.length, submitGuess]);

  // Handle physical keyboard
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (state.status !== 'in_progress' || state.submitting) return;

      if (e.key === 'Enter') {
        e.preventDefault();
        onEnter();
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        onBackspace();
      } else if (/^[a-zA-Z]$/.test(e.key)) {
        onKeyPress(e.key.toLowerCase());
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [state.status, state.submitting, onEnter, onBackspace, onKeyPress]);

  // Load active game on mount
  useEffect(() => {
    loadActiveGame();
    return () => {
      fetchIdRef.current++;
    };
  }, [loadActiveGame]);

  return {
    ...state,
    startNewGame,
    submitGuess,
    onKeyPress,
    onBackspace,
    onEnter,
  };
}
